import { calcValue } from '@nfctron/nexus-core';
import { type TVariableBorderRadiusKeys, type TVariableColorKeys, type TVariableFontSizeKeys, type ThemeColor, cn } from '@nfctron/nexus-react';

import {
	Accordion,
	ActionIcon,
	Button,
	Checkbox,
	Input,
	InputWrapper,
	Loader,
	type MantineColorsTuple,
	type MantineThemeOverride,
	Menu,
	Modal,
	PasswordInput,
	Select,
	Stepper,
	Text,
	TextInput,
	Title,
	Tooltip,
	createTheme,
} from '@mantine/core';

import { type ThemeCssVariables, getBorderRadius, getFontSize, getThemeColor } from '@root/config/src/lib/utils/tw.config';

/**
 * Mantine components configuration override
 * @type {MantineThemeOverride["components"]}
 */
const mantineComponentsConfig: MantineThemeOverride['components'] = {
	/** Button component */
	Button: Button.extend({
		classNames(theme, props, ctx) {
			return {
				root: cn('font-semibold', {
					'rounded-full': props.radius === 'xl',
				}),
			};
		},
		defaultProps: {
			radius: 'md',
		},
	}),
	/** ActionIcon component */
	ActionIcon: ActionIcon.extend({
		classNames(theme, props, ctx) {
			return {
				root: cn({
					'rounded-full': props.radius === 'xl',
				}),
			};
		},
		defaultProps: {
			radius: 'xl',
		},
	}),
	/* InputWrapper component */
	InputWrapper: InputWrapper.extend({
		classNames(theme, props, ctx) {
			return {
				required: 'text-gray-500',
				error: 'text-base',
				description: 'text-gray-500 text-base',
				label: 'text-gray-600 font-semibold text-base',
			};
		},
	}),
	/* Input component */
	Input: Input.extend({
		defaultProps: {
			radius: 'md',
		},
		classNames(theme, props, ctx) {
			return {
				input: cn('text-base border h-10.5 leading-[42px] text-lucky-point-800', 'border-zinc-200 focus:border-lucky-point-500', {
					'border-red-600 text-red-600 focus:border-red-700': !!props.error,
				}),
			};
		},
	}),
	/** TextInput component */
	TextInput: TextInput.extend({
		classNames(theme, props, ctx) {
			return {
				error: 'text-red-600 text-sm',
				description: 'text-sm',
				label: 'mb-0.5 text-base',
				input: 'text-base',
			};
		},
		defaultProps: {
			radius: 'md',
		},
	}),
	/** PasswordInput component */
	PasswordInput: PasswordInput.extend({
		classNames(theme, props, ctx) {
			return {
				innerInput: 'h-10.5 leading-[42px]',
				error: 'text-red-600 text-sm',
			};
		},
	}),
	/** Select component */
	Select: Select.extend({
		classNames(theme, props, ctx) {
			return {
				dropdown: 'rounded-md',
				option: 'rounded-md',
			};
		},
		defaultProps: {
			radius: 'md',
		},
	}),
	/* Modal component */
	Modal: Modal.extend({
		classNames(theme, props, ctx) {
			return {
				content: 'rounded-lg',
				header: 'pl-4 pr-3.5 py-4',
				title: 'text-lucky-point-500 font-bold text-lg',
				body: 'text-gray-500 pl-4 pr-4 pb-4',
			};
		},
	}),
	/** Menu component */
	Menu: Menu.extend({
		classNames(theme, props, ctx) {
			return {
				dropdown: 'rounded-md min-w-[225px]',
			};
		},
	}),
	/** Stepper component */
	Stepper: Stepper.extend({
		classNames(theme, props, ctx) {
			return {
				step: 'data-[hidden=true]:hidden! disabled:opacity-50!',
				content: 'pt-4',
				stepIcon:
					'rounded-full data-completed:bg-green-500 data-progress:border-lucky-point-500 data-progress:bg-lucky-point-500 data-progress:text-white',
				stepCompletedIcon: 'rounded-full border-green-500 bg-green-500',
			};
		},
	}),
	/** Title component */
	Title: Title.extend({
		classNames(theme, props, ctx) {
			return {
				root: 'font-sans',
			};
		},
	}),
	/** Text */
	Text: Text.extend({
		classNames(theme, props, ctx) {
			return {
				root: 'font-sans',
			};
		},
	}),
	/** Tooltip component */
	Tooltip: Tooltip.extend({
		classNames(theme, props, ctx) {
			return {
				tooltip: 'rounded-lg text-base max-w-[250px] w-max',
			};
		},
		defaultProps: {
			multiline: true,
			withArrow: true,
			events: { hover: true, focus: true, touch: true },
		},
	}),
	/** Checkbox component */
	Checkbox: Checkbox.extend({
		classNames(theme, props, ctx) {
			return {
				label: 'text-gray-500 text-sm',
				input: 'rounded-md border border-gray-300 checked:bg-purple-heart-500',
			};
		},
	}),
	/** Accordion component */
	Accordion: Accordion.extend({
		classNames(theme, props, ctx) {
			return {
				item: 'overflow-hidden border border-gray-100 data-active:border-gray-200!',
				label: 'font-semibold text-gray-600 text-sm',
				control: 'bg-transparent rounded-none!',
				content: 'bg-gray-50 p-4!',
			};
		},
	}),
	/** Loader */
	Loader: Loader.extend({
		defaultProps: {
			type: 'dots',
		},
	}),
};

/**
 * Mantine theme config from provided TailwindCSS configuration
 */
const mantineConfigFromTailwind = calcValue(() => {
	const colorShades = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;
	type Colors = TVariableColorKeys<ThemeCssVariables> | ThemeColor;
	return {
		radius: {
			xs: getBorderRadius('xs'),
			sm: getBorderRadius('sm'),
			md: getBorderRadius('md'),
			lg: getBorderRadius('lg'),
			xl: getBorderRadius('xl'),
			'2xl': getBorderRadius('2xl'),
			'3xl': getBorderRadius('3xl'),
			'4xl': getBorderRadius('4xl'),
			full: '9999px',
		} satisfies Record<TVariableBorderRadiusKeys<ThemeCssVariables>, string>,
		fontSizes: {
			'2xs': getFontSize('2xs').fontSize,
			xs: getFontSize('xs').fontSize,
			sm: getFontSize('sm').fontSize,
			base: getFontSize('base').fontSize,
			lg: getFontSize('lg').fontSize,
			xl: getFontSize('xl').fontSize,
			'2xl': getFontSize('2xl').fontSize,
			'3xl': getFontSize('3xl').fontSize,
			'4xl': getFontSize('4xl').fontSize,
			'5xl': getFontSize('5xl').fontSize,
			'6xl': getFontSize('6xl').fontSize,
			'7xl': getFontSize('7xl').fontSize,
			'8xl': getFontSize('8xl').fontSize,
			'9xl': getFontSize('9xl').fontSize,
		} satisfies Record<TVariableFontSizeKeys<ThemeCssVariables>, string>,
		colors: {
			gray: colorShades.map((shade) => getThemeColor('gray', shade, false)) as unknown as MantineColorsTuple,
			'purple-heart': colorShades.map((shade) => getThemeColor('purple-heart', shade, false)) as unknown as MantineColorsTuple,
			'lucky-point': colorShades.map((shade) => getThemeColor('lucky-point', shade, false)) as unknown as MantineColorsTuple,
			white: colorShades.map((shade) => getThemeColor('white', shade, false)) as unknown as MantineColorsTuple,
			black: colorShades.map((shade) => getThemeColor('black', shade, false)) as unknown as MantineColorsTuple,
			slate: colorShades.map((shade) => getThemeColor('slate', shade, false)) as unknown as MantineColorsTuple,
			zinc: colorShades.map((shade) => getThemeColor('zinc', shade, false)) as unknown as MantineColorsTuple,
			neutral: colorShades.map((shade) => getThemeColor('neutral', shade, false)) as unknown as MantineColorsTuple,
			stone: colorShades.map((shade) => getThemeColor('stone', shade, false)) as unknown as MantineColorsTuple,
			red: colorShades.map((shade) => getThemeColor('red', shade, false)) as unknown as MantineColorsTuple,
			orange: colorShades.map((shade) => getThemeColor('orange', shade, false)) as unknown as MantineColorsTuple,
			amber: colorShades.map((shade) => getThemeColor('amber', shade, false)) as unknown as MantineColorsTuple,
			yellow: colorShades.map((shade) => getThemeColor('yellow', shade, false)) as unknown as MantineColorsTuple,
			lime: colorShades.map((shade) => getThemeColor('lime', shade, false)) as unknown as MantineColorsTuple,
			green: colorShades.map((shade) => getThemeColor('green', shade, false)) as unknown as MantineColorsTuple,
			emerald: colorShades.map((shade) => getThemeColor('emerald', shade, false)) as unknown as MantineColorsTuple,
			teal: colorShades.map((shade) => getThemeColor('teal', shade, false)) as unknown as MantineColorsTuple,
			cyan: colorShades.map((shade) => getThemeColor('cyan', shade, false)) as unknown as MantineColorsTuple,
			sky: colorShades.map((shade) => getThemeColor('sky', shade, false)) as unknown as MantineColorsTuple,
			blue: colorShades.map((shade) => getThemeColor('blue', shade, false)) as unknown as MantineColorsTuple,
			indigo: colorShades.map((shade) => getThemeColor('indigo', shade, false)) as unknown as MantineColorsTuple,
			violet: colorShades.map((shade) => getThemeColor('violet', shade, false)) as unknown as MantineColorsTuple,
			purple: colorShades.map((shade) => getThemeColor('purple', shade, false)) as unknown as MantineColorsTuple,
			fuchsia: colorShades.map((shade) => getThemeColor('fuchsia', shade, false)) as unknown as MantineColorsTuple,
			pink: colorShades.map((shade) => getThemeColor('pink', shade, false)) as unknown as MantineColorsTuple,
			rose: colorShades.map((shade) => getThemeColor('rose', shade, false)) as unknown as MantineColorsTuple,
		} satisfies Record<Colors, MantineColorsTuple>,
	};
});

/**
 * Mantine theme configuration override
 * @type {MantineThemeOverride}
 * @export
 */
export const mantineThemeConfig = createTheme({
	...mantineConfigFromTailwind,
	primaryColor: 'lucky-point',
	primaryShade: 5,
	fontFamily: 'Poppins',
	components: mantineComponentsConfig,
});
